import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import DropDownItem from './DropDownItem';
import SvgIcon from "../icons/SvgIcon";
import Caret from '../icons/Caret';

const DropDown = ({
    closeOnSelect = true,
    containerRef = {},
    title = 'Select',
    selected = {},
    options = [],
    onChange = () => { },
    onToggle = () => { },
    onClick = () => { },
    isScrollToActiveItem,
    blockClickToggle,
    isStaticTitle,
    className,
    disabled,
    noCaret,
    dropUp,
    id,
}) => {
    const [search, setSearch] = useState("");
    const [isOpen, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    const filteredOptions = useMemo(() => {
        return search.length === 0 ? options :
            options.filter((option) => Object.hasOwn(option, "searchName") ? // searchName is used when name key is React.element
                option.searchName.toLowerCase().includes(search.toLowerCase()) || search.toLowerCase().includes(option.searchName.toLowerCase()) :
                option.name.toLowerCase().includes(search.toLowerCase()) || search.toLowerCase().includes(option.name.toLowerCase())
            );
    }, [search, options])

    useEffect(() => {
        document.addEventListener('click', onHandleDocumentClick);

        return () =>
            document.removeEventListener('click', onHandleDocumentClick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownRef, isOpen]);

    useEffect(() => {
        handleToggle(isOpen);
        setSearch("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    const handleToggle = isOpen => {
        onToggle(isOpen);

        if (isOpen && selected.value) {
            const activeItem = document.getElementById('option_' + selected.value.toLowerCase());

            if (activeItem && isScrollToActiveItem) {
                setTimeout(() => { activeItem.scrollIntoView({ block: "center" }) })
            }
        }
    }

    const onHandleDocumentClick = (e) => {
        if (isOpen && dropdownRef.current) {
            if (
                !dropdownRef.current.contains(e.target) ||
                (closeOnSelect && e.target.closest('.DropDown__menu'))
            ) {
                setOpen(false);
            }
        }
    };

    const handleClick = (e) => {
        e.preventDefault();

        if (disabled || blockClickToggle) return;

        onClick();

        setOpen(!isOpen);
    };

    const handleChange = (option) => {
        onChange(option);
        closeOnSelect && setOpen(false);
    }

    const getDropUp = () => {
        const containerElement = containerRef.current;

        if (!containerElement || !dropdownRef.current) {
            return dropUp
        }

        const dropdownBottom = dropdownRef.current.getBoundingClientRect().bottom;
        const containerBottom = containerElement.getBoundingClientRect().bottom;
        const dropDownHeight = dropdownRef.current.children[0].offsetHeight;

        return containerBottom < dropdownBottom + dropDownHeight;
    }

    const dropdownTitle = (
        <div className='DropDown__title-wrapper'>
            <span className={classNames("DropDown__title", selected.value)}>
                {selected.icon ? <SvgIcon icon={selected.icon} className="DropDown__title__icon" /> : ''}
                {!isStaticTitle && (selected.name || title)}
                {isStaticTitle && title}
            </span>
        </div>
    );

    return (
        <div className={'DropDown__wrap'} ref={dropdownRef}>
            <div
                className={classNames('DropDown', className, {
                    disabled: disabled,
                    open: isOpen && !disabled,
                    'drop-up': getDropUp(),
                })}
            >
                <div
                    className="DropDown__toggle"
                    id={id}
                    onClick={handleClick}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                >
                    {dropdownTitle}
                    {!noCaret && <Caret isOpen={isOpen} />}
                </div>
                <div
                    className="DropDown__menu"
                    aria-labelledby={id}
                >
                    <ul className="DropDown__list">
                        {filteredOptions.map(option => (
                            <DropDownItem
                                key={option.value}
                                className={option.className}
                                onSelect={() => handleChange(option)}
                                selected={option.value === selected.value}
                                title={typeof (option.name) === 'string' ? option.name : ''}
                                icon={option.icon}
                                value={option.value}
                                disabled={option.disabled}
                            >
                                {option.name}
                            </DropDownItem>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

DropDown.propTypes = {
    disabled: PropTypes.bool,
    selected: PropTypes.object,
    containerRef: PropTypes.object,
    className: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
        value: PropTypes.any.isRequired,
        searchName: PropTypes.string
    })).isRequired,
    onChange: PropTypes.func.isRequired,
    dropUp: PropTypes.bool,
    noCaret: PropTypes.bool,
    isScrollToActiveItem: PropTypes.bool,
    onClick: PropTypes.func,
    id: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    closeOnSelect: PropTypes.bool,
    blockClickToggle: PropTypes.bool,
    isStaticTitle: PropTypes.bool,
};

export default DropDown;